import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

import { cn } from '@/lib/cn';

export interface ComboboxOption {
  id: number | string;
  label: string;
  hint?: string;
}

interface ComboboxProps {
  value: number | string | null | undefined;
  options: ComboboxOption[];
  onChange: (id: number | string | null) => void;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  emptyMessage?: string;
}

/**
 * Single-select con búsqueda inline. Reemplaza un <select> simple cuando hay
 * muchas opciones y conviene poder tipear para filtrar.
 *
 * El input muestra el label de la opción seleccionada y permite filtrar.
 * Cuando abre, lista hasta ~200 resultados.
 */
export function Combobox({
  value,
  options,
  onChange,
  placeholder = 'Seleccionar…',
  clearable = true,
  disabled = false,
  className,
  inputClassName,
  emptyMessage = 'Sin resultados',
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)),
    [options, value],
  );

  // Cuando cambia el valor externo, sincronizamos el input (cerrado).
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open, selected]);

  // Cerrar al clickear fuera.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Ordenar alfabéticamente con locale es-AR (ignora mayúsculas y acentos).
    const sorted = options
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    if (!q) return sorted.slice(0, 200);
    return sorted
      .filter((o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q))
      .slice(0, 200);
  }, [options, query]);

  const displayValue = open ? query : selected?.label ?? '';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          type="text"
          className={cn('input pr-12', inputClassName)}
          placeholder={placeholder}
          value={displayValue}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
          {clearable && selected && !disabled && (
            <button
              type="button"
              className="text-notion-400 hover:text-notion-800"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setQuery('');
              }}
              aria-label="Limpiar"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={14} className="text-notion-400" />
        </div>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-notion-200 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-notion-500">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-notion-100',
                  String(o.id) === String(value) && 'bg-notion-50 font-medium',
                )}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {o.label}
                {o.hint && <span className="text-xs text-notion-500 ml-2">{o.hint}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
